import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ToastService } from '../../../../core/services/toast.service';
import { PreferencesService } from '../../../../core/services/preferences.service';
import { UpdatePreferencesDto } from '../../../../core/interfaces/preferences.interface';
import { PushNotificationService } from '../../../../core/services/push-notification.service';

@Component({
  selector: 'app-preferences',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './preferences.component.html',
  styleUrls: ['./preferences.component.scss'],
})
export class PreferencesComponent implements OnInit {
  // Notification preferences state
  notificationEmail = true;
  notificationSms = false;
  notificationPush = false;
  saving = false;
  loading = false;
  pushProcessing = false;

  constructor(
    private toastService: ToastService,
    private preferencesService: PreferencesService,
    private pushNotificationService: PushNotificationService
  ) {}

  ngOnInit(): void {
    this.loading = true;
    this.preferencesService.getMyPreferences().subscribe({
      next: (res) => {
        const prefs = res?.notification || {};
        this.notificationEmail = !!prefs.email;
        this.notificationSms = !!prefs.sms;
        this.notificationPush = !!prefs.push;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        // Keep defaults on error; optionally show info
      },
    });
  }

  async onPushPreferenceChange(enabled: boolean): Promise<void> {
    if (!enabled) {
      await this.pushNotificationService.disableForCurrentBrowser();
      return;
    }

    await this.ensurePushEnabled();
  }

  async savePreferences(): Promise<void> {
    this.saving = true;

    if (this.notificationPush) {
      await this.ensurePushEnabled();
    } else {
      await this.pushNotificationService.disableForCurrentBrowser();
    }

    const payload: UpdatePreferencesDto = {
      notification: {
        email: this.notificationEmail,
        sms: this.notificationSms,
        push: this.notificationPush,
      },
    };

    this.preferencesService.updateMyPreferences(payload).subscribe({
      next: (res) => {
        const prefs = res?.notification || {};
        this.notificationEmail = !!prefs.email;
        this.notificationSms = !!prefs.sms;
        this.notificationPush = !!prefs.push;
        this.toastService.success('Préférences de notifications enregistrées');
        this.saving = false;
      },
      error: () => {
        this.toastService.error("Impossible d'enregistrer les préférences");
        this.saving = false;
      },
    });
  }

  private async ensurePushEnabled(): Promise<boolean> {
    this.pushProcessing = true;

    try {
      const state = await this.pushNotificationService.enableForCurrentUser();

      if (!state) {
        this.notificationPush = false;
        this.toastService.error(
          'Les notifications push ne sont pas configurées pour cet environnement'
        );
        return false;
      }

      if (state.permission === 'unsupported') {
        this.notificationPush = false;
        this.toastService.error(
          'Votre navigateur ne supporte pas les notifications push'
        );
        return false;
      }

      if (state.permission === 'denied') {
        this.notificationPush = false;
        this.toastService.error(
          'Les notifications push sont bloquées dans votre navigateur'
        );
        return false;
      }

      if (!state.optedIn) {
        this.notificationPush = false;
        this.toastService.error(
          "Impossible d'activer les notifications push"
        );
        return false;
      }

      this.notificationPush = true;
      this.toastService.success('Notifications push activées');
      return true;
    } catch {
      this.notificationPush = false;
      return false;
    } finally {
      this.pushProcessing = false;
    }
  }
}
